import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { uuid } from "@/interfaces";
import {
  createTaskWorkflowCategory,
  createTaskWorkflowItem,
  deleteTaskWorkflowCategory,
  deleteTaskWorkflowItem,
  getTaskWorkflow,
} from "./task-workflow.slice";
import { TaskWorkflowCategory } from "@/models/task-workflow.model";
import { toast } from "sonner";
import { apiRefusalOf } from "@/lib/appointment-field-actions";

const inUseRefusal = (error: unknown): string | null => {
  const refusal = apiRefusalOf(error);
  return refusal?.code === "in_use" ? refusal.message : null;
};

export function useTaskWorkflow(id: uuid) {
  const dispatch = useAppDispatch();
  const { list, loading } = useAppSelector((state) => state.taskWorkflow);
  const [newTask, setNewTask] = useState<{ [key: string]: string }>({});
  const [addDialog, setAddDialog] = useState(false);

  function load() {
    dispatch(getTaskWorkflow(id));
  }

  function clearCategoryTask(id: string) {
    setNewTask((prev) => {
      const newObj = { ...prev };
      delete newObj[id];
      return newObj;
    });
  }

  async function handleAddTask(category: uuid) {
    const title = newTask[category]?.trim();
    if (!title) return;
    try {
      await dispatch(createTaskWorkflowItem({ service: id, category, title })).unwrap();
      clearCategoryTask(category);
      toast.success("Success", {
        description: "Task workflow item successfully added",
      });
    } catch {
      toast.error("Oops!!", {
        description: "Something went wrong while adding task workflow item",
      });
    }
  }

  async function handleSubmit(values: Partial<TaskWorkflowCategory>) {
    try {
      await dispatch(createTaskWorkflowCategory({ service: id, title: values.title ?? "" })).unwrap();
      setAddDialog(false);
      toast.success("Success", {
        description: "Task workflow category successfully added",
      });
    } catch {
      toast.error("Oops!!", {
        description: "Something went wrong while adding task workflow category",
      });
    }
  }

  async function handleDeleteCategory(category: uuid) {
    try {
      await dispatch(deleteTaskWorkflowCategory({ service: id, category })).unwrap();
      toast.success("Success", {
        description: "Task workflow category successfully deleted",
      });
    } catch (error) {
      toast.error("Oops!!", {
        description: inUseRefusal(error) ?? "Something went wrong while deleting task workflow category",
      });
    }
  }

  async function handleDeleteTask(task: uuid) {
    try {
      await dispatch(deleteTaskWorkflowItem({ service: id, task })).unwrap();
      toast.success("Success", {
        description: "Task workflow successfully deleted",
      });
    } catch (error) {
      toast.error("Oops!!", {
        description: inUseRefusal(error) ?? "Something went wrong while deleting task workflow ",
      });
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  return {
    list,
    loading,
    newTask,
    setNewTask,
    handleAddTask,
    addDialog,
    setAddDialog,
    handleSubmit,
    handleDeleteCategory,
    handleDeleteTask,
  };
}
